import { useState, useEffect, useRef } from "react";
import { useSEO } from "@/hooks/use-seo";
import { buildLandingJsonLd } from "@/data/marketing-jsonld";
import { HOME_SEO } from "@/data/marketing-seo";
import { FeatureRequestDialog } from "@/components/feature-request-dialog";
import { PublicHeader } from "@/components/public-header";
import commonAreaImage from "@assets/Common_area_2_1776068733503.jpg";
import travisHeadshotImg from "@assets/travis_headshot_nobg.png";
import { PublicFooter } from "@/components/public-footer";
import { FeatureExplorer } from "@/components/feature-explorer";
import { PROOF_TILES } from "@/data/proof-tiles";
import { StartFreeButton, TrustLine, START_FREE_LABEL } from "@/components/marketing/cta";
import { PricingCards } from "@/components/marketing/pricing-cards";
import { track } from "@/lib/marketing/analytics";
import { signupHref } from "@/lib/marketing/links";
import { TRIAL_DAYS } from "@/lib/marketing/pricing-copy";
import { Link, useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import {
  TrendingUp,
  PlusCircle,
  CheckCircle,
  Zap,
  Users,
  Shield,
  ClipboardList,
  MessageSquare,
  RefreshCw,
  Building2,
  Sparkles,
  X,
} from "lucide-react";

// Keep V1's savings alongside the shared V2 product stats, not portfolio metrics.
const stats = [
  ...PROOF_TILES,
  { value: "$250+", label: "Saved per Booking vs. PadSplit Fees" },
];


const caseStudyTimeline = [
  { month: "Year 1", milestone: "Bought 3 properties and solidified systems to run without PadSplit", metric: "3 properties, 30 rooms" },
  { month: "The Pivot", milestone: "Decided to scale rather than stay stuck in the weeds — switched to PadSplit and refined systems", metric: "5 properties, 54 rooms" },
  { month: "Month 1–4", milestone: "Rapid acquisition with repeatable underwriting and launch playbook", metric: "7 properties, 78 rooms" },
  { month: "Month 5–8", milestone: "Automated pricing, messaging, and maintenance ops across portfolio", metric: "11 properties, 132 rooms" },
  { month: "Month 9–12", milestone: "Full portfolio operating at scale — 12 new properties in 12 months", metric: "15 properties, 181 rooms" },
];

function useScrollAnimation() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

function AnimatedSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, visible } = useScrollAnimation();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}


export default function Landing() {
  const [, navigate] = useLocation();

  useSEO({
    title: HOME_SEO.title,
    description: HOME_SEO.description,
    ogType: "website",
    path: HOME_SEO.path,
    jsonLd: buildLandingJsonLd(),
  });

  return (
    <div className="min-h-screen bg-white">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-brand-accent focus:text-white focus:rounded-md focus:font-medium"
      >
        Skip to main content
      </a>
      <PublicHeader />

      {/* ── Hero ── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-brand-accent/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl" />
        </div>

        <section id="hero" className="relative pt-28 md:pt-32 pb-16 md:pb-20" data-testid="section-hero">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1] tracking-tight" data-testid="text-hero-heading">
                  The All-in-One Platform{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-accent to-[#ffcc00]">Built for PadSplit Hosts</span>
                </h1>
                <p className="text-lg md:text-xl text-blue-100 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                  PadPulse is the dedicated management platform for PadSplit hosts — underwrite deals before you buy, automate operations once you own, and sync everything with PadSplit from day one to full-scale portfolio.
                </p>
                <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4">
                  <StartFreeButton ctaId="hero-start-free" location="home_hero" className="px-8 py-6 text-base" />
                </div>
                <TrustLine className="mt-4 text-center lg:text-left" />
              </div>

              <div className="hidden lg:flex justify-center items-center">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-brand-accent/20 to-blue-500/20 rounded-2xl blur-xl" />
                  <img
                    alt="Modern and inviting co-living common area"
                    className="relative w-full max-w-lg rounded-2xl shadow-2xl border border-white/10 object-cover"
                    src={commonAreaImage}
                    data-testid="img-hero-coliving"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto" data-testid="hero-stats">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center max-w-[16rem]">
                  <div className="text-3xl md:text-4xl font-bold text-brand-accent mb-1">{stat.value}</div>
                  <div className="text-xs md:text-sm text-blue-200">{stat.label}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-blue-200">
              Continuous Auto Draft requires beta access and enabling; signup or a paid plan alone does not grant access.
              {" "}60s refers to the send/reconciliation cycle, not guaranteed inbound arrival.
            </p>
          </div>
        </section>

      </div>

      <main id="main-content">
        {/* ── Features: product evidence immediately below the hero ── */}
        <section id="features" className="py-20 md:py-28 bg-gray-50" data-testid="section-features">
          <div className="container mx-auto px-6">
            <AnimatedSection>
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-brand-accent/10 rounded-full px-4 py-1.5 text-sm font-medium text-brand-accent mb-6">
                  <Zap size={14} />
                  The Complete Co-Living Host Toolkit
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="text-features-heading">
                  One Platform. Every Tool. Zero Compromise.
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Manage your houses on PadSplit, off PadSplit, or both. Explore every workspace with actual app screenshots.
                  Hover or pause the automatic tour to inspect a section, and select a screenshot to enlarge it.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={100}>
              <FeatureExplorer
                previewMode="screenshots"
                onCtaClick={() => {
                  track("cta_clicked", { cta_id: "feature-explorer-start-free", location: "home_features" });
                  navigate(signupHref());
                }}
                ctaLabel={START_FREE_LABEL}
              />
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div className="mt-8 bg-gradient-to-br from-brand/5 to-brand-accent/5 rounded-xl border-2 border-dashed border-brand-accent/30 p-8 flex flex-col sm:flex-row items-center justify-between gap-6" data-testid="card-feature-request-center">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-brand-accent/10 text-brand-accent flex-shrink-0">
                    <PlusCircle size={28} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">... and many more on the way</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      Have ideas? Help shape the future of PadPulse with your feature requests.
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <FeatureRequestDialog />
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* ── Built for PadSplit ── */}
        <section className="py-20 md:py-28 bg-white border-t border-gray-100" data-testid="section-built-for-padsplit">
          <div className="container mx-auto px-6">
            <AnimatedSection>
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-indigo-500/10 rounded-full px-4 py-1.5 text-sm font-medium text-indigo-600 mb-6">
                  <RefreshCw size={14} />
                  Connects to your PadSplit account
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Everything a PadSplit Host Needs, in One Place
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Unlike generic property management tools, PadPulse connects to your PadSplit account — so your data, members, and operations stay in sync automatically.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={100}>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
                {[
                  {
                    icon: MessageSquare,
                    title: "Messages",
                    description: "Unified inbox — SMS, PadSplit Messenger, and vendor threads in one place.",
                    color: "text-teal-500",
                    bg: "bg-teal-500/10",
                    href: "/features/messages",
                  },
                  {
                    icon: Building2,
                    title: "Properties",
                    description: "Room management, occupancy sync, listings, and Zillow import across your portfolio.",
                    color: "text-indigo-500",
                    bg: "bg-indigo-500/10",
                    href: "/features",
                  },
                  {
                    icon: TrendingUp,
                    title: "Pipeline",
                    description: "Market research, buy box screening, and underwriting.",
                    color: "text-green-500",
                    bg: "bg-green-500/10",
                    href: "/research",
                  },
                  {
                    icon: Users,
                    title: "Members",
                    description: "PadSplit member profiles, vehicle tracking, and booking requests.",
                    color: "text-violet-500",
                    bg: "bg-violet-500/10",
                    href: "/features",
                  },
                  {
                    icon: ClipboardList,
                    title: "Operations",
                    description: "Tasks with automated rules, service forms, member cleaning schedules, team to-dos, supplies, and dynamic room pricing.",
                    color: "text-orange-500",
                    bg: "bg-orange-500/10",
                    href: "/features/maintenance",
                  },
                  {
                    icon: Shield,
                    title: "Vendors",
                    description: "Vendor directory, Stripe-powered payments, and auto-routing.",
                    color: "text-blue-500",
                    bg: "bg-blue-500/10",
                    href: "/features/maintenance",
                  },
                  {
                    icon: Zap,
                    title: "Revenue",
                    description: "P&L, income, expenses, and Mercury statement sync.",
                    color: "text-brand-accent",
                    bg: "bg-brand-accent/10",
                    href: "/features/numbers",
                  },
                  {
                    icon: RefreshCw,
                    title: "Connections",
                    description: "PadSplit sync, Stripe, Mercury Bank, and smart lock integrations.",
                    color: "text-rose-500",
                    bg: "bg-rose-500/10",
                    href: "/features",
                  },
                ].map((item, i) => (
                  <Link
                    key={i}
                    href={item.href}
                    className="flex gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all group"
                    data-testid={`card-padsplit-integration-${i}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${item.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <item.icon size={18} className={item.color} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-indigo-600 transition-colors">{item.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">{item.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* ── Why PadSplit Hosts Choose PadPulse ── */}
        <section
          id="why-padpulse"
          className="py-20 md:py-28 bg-gradient-to-b from-gray-950 to-gray-900"
          data-testid="section-why-padpulse"
        >
          <div className="container mx-auto px-6">
            <AnimatedSection>
              <div className="max-w-3xl mx-auto text-center mb-14">
                <div className="inline-flex items-center gap-2 bg-brand-accent/10 rounded-full px-4 py-1.5 text-sm font-medium text-brand-accent mb-6">
                  <Sparkles size={14} />
                  Why PadSplit Hosts Choose PadPulse
                </div>
                <h2
                  className="text-3xl md:text-4xl font-bold text-white mb-6"
                  data-testid="text-why-padpulse-heading"
                >
                  Airbnb hosts have PMSs. PadSplit hosts didn't — until now.
                </h2>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Every other lodging vertical has a dedicated property management system. Co-living hosts have been stuck stitching together spreadsheets, the PadSplit dashboard, and a pile of text threads. PadPulse is the first PMS purpose-built for rent-by-the-room operators.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={100}>
              <div className="max-w-5xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-full px-4 py-1.5 text-sm font-semibold text-red-300">
                      <X size={14} />
                      The old way
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 text-sm font-semibold text-emerald-300">
                      <CheckCircle size={14} />
                      The PadPulse way
                    </div>
                  </div>
                </div>
                {[
                  {
                    old: "Manually re-keying rent rolls and listing edits between spreadsheets and PadSplit",
                    pulse: "Bi-directional PadSplit auto-sync for rooms, rates, members, and messages",
                  },
                  {
                    old: "Juggling PadSplit Messenger, personal SMS, and email to talk to members and vendors",
                    pulse: "Unified inbox with SMS, PadSplit Messenger, and internal threads in one place",
                  },
                  {
                    old: "Underwriting deals in fragile spreadsheets that nobody else on the team can read",
                    pulse: "Built-in underwriting calculator with branded PDF exports and shareable deal pages",
                  },
                  {
                    old: "Chasing vendors over text and Venmo with no record of who did what or got paid",
                    pulse: "Vendor portal with task auto-routing, two-way chat, and Stripe-powered payouts",
                  },
                  {
                    old: "Hunting through bank statements to figure out how a property is really doing",
                    pulse: "Live P&L and device health on one dashboard per property",
                  },
                ].map((row, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4" data-testid={`row-comparison-${i}`}>
                    <div className="flex items-start gap-3 bg-gray-900/60 border border-gray-800 rounded-xl p-5" data-testid={`text-old-way-${i}`}>
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center mt-0.5">
                        <X className="text-red-400" size={16} />
                      </div>
                      <p className="text-gray-400 text-sm md:text-base leading-relaxed line-through decoration-red-500/40 decoration-1">
                        {row.old}
                      </p>
                    </div>
                    <div className="flex items-start gap-3 bg-gradient-to-br from-brand/15 to-emerald-500/5 border border-emerald-500/20 rounded-xl p-5" data-testid={`text-padpulse-way-${i}`}>
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center mt-0.5">
                        <CheckCircle className="text-emerald-400" size={16} />
                      </div>
                      <p className="text-white text-sm md:text-base leading-relaxed font-medium">
                        {row.pulse}
                      </p>
                    </div>
                  </div>
                ))}
                <div className="text-center mt-10">
                  <StartFreeButton ctaId="why-padpulse-start-free" location="home_why_padpulse" />
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* ── Founder Case Study ── */}
        <section id="case-study" className="py-20 md:py-28 bg-gray-50" data-testid="section-case-study">
          <div className="container mx-auto px-6">
            <AnimatedSection>
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-brand/5 rounded-full px-4 py-1.5 text-sm font-medium text-brand mb-6">
                  <Building2 size={14} />
                  Founder Story
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="text-case-study-heading">
                  From 3 Properties to 15 in One Year
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  How Travis scaled a rent-by-the-room portfolio to 15 properties and 181 rooms — and turned the systems he built along the way into the platform you see today.
                </p>
              </div>
            </AnimatedSection>

            <div className="max-w-4xl mx-auto">
              <AnimatedSection delay={100}>
                <Card className="bg-white border border-gray-100 shadow-sm overflow-hidden" data-testid="card-case-study">
                  <CardContent className="p-0">
                    <div className="grid md:grid-cols-5">
                      <div className="md:col-span-2 bg-gradient-to-br from-[#0a1628] to-brand p-6 md:p-8 flex flex-col justify-center">
                        <div className="w-64 h-64 rounded-full overflow-hidden bg-brand-accent/20 mb-4 flex-shrink-0 mx-auto">
                          <img src={travisHeadshotImg} alt="Travis Schwartz" className="w-full h-full object-cover object-top" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-1" data-testid="text-case-study-name">Travis S.</h3>
                        <p className="text-blue-200 text-sm mb-4">Founder, PadPulse</p>
                        <div className="space-y-3">
                          <div className="bg-white/10 rounded-lg p-3">
                            <div className="text-2xl font-bold text-brand-accent">15</div>
                            <div className="text-xs text-blue-200">Properties (12 in 12 months)</div>
                          </div>
                          <div className="bg-white/10 rounded-lg p-3">
                            <div className="text-2xl font-bold text-brand-accent">181</div>
                            <div className="text-xs text-blue-200">Rooms under management</div>
                          </div>
                          <div className="bg-white/10 rounded-lg p-3">
                            <div className="text-2xl font-bold text-brand-accent">91%*</div>
                            <div className="text-xs text-blue-200">Stabilized occupancy rate</div>
                          </div>
                        </div>
                      </div>

                      <div className="md:col-span-3 p-6 md:p-8">
                        <blockquote className="text-gray-700 text-sm leading-relaxed italic border-l-2 border-brand-accent pl-4 mb-6" data-testid="text-case-study-quote">
                          "I bought my first three properties and spent a year building every system from scratch — underwriting, operations, member management — all without PadSplit. Once I had the playbook down, I realized I'd rather scale than stay stuck in the weeds. After switching to PadSplit and refining my systems, I scaled 12 properties in 12 months. Every feature in PadPulse exists because I needed it myself."
                        </blockquote>

                        <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">The Scale-Up Timeline</h4>
                        <div className="space-y-3">
                          {caseStudyTimeline.map((step, i) => (
                            <div
                              key={i}
                              className="flex items-start gap-3"
                              data-testid={`case-study-step-${i}`}
                            >
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-accent/10 flex items-center justify-center mt-0.5">
                                <span className="text-xs font-bold text-brand-accent">{i + 1}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2">
                                  <span className="text-xs font-semibold text-brand">{step.month}</span>
                                  <span className="text-xs text-gray-400 whitespace-nowrap">{step.metric}</span>
                                </div>
                                <p className="text-sm text-gray-600">{step.milestone}</p>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="mt-6 pt-4 border-t border-gray-100">
                          <p className="text-sm text-gray-600 leading-relaxed">
                            The systems Travis built to manage his own portfolio became the foundation for PadPulse. Every underwriting template, every automation rule, every maintenance workflow — battle-tested across 74 rooms before a single line of product code was written.
                          </p>
                          <p className="text-sm text-gray-700 font-medium mt-3">
                            Next six months: on track for 250 rooms across 25 properties.
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            *Stabilized occupancy for properties older than 90 days
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* ── Pricing Plans ── */}
        <section id="pricing" className="py-20 md:py-28 bg-white" data-testid="section-pricing">
          <div className="container mx-auto px-6">
            <AnimatedSection>
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 bg-brand/5 rounded-full px-4 py-1.5 text-sm font-medium text-brand mb-6">
                  <Zap size={14} />
                  Plans for Every Stage
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="text-pricing-heading">
                  Simple, Transparent Pricing
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-2">
                  One price per property, with everything included. {TRIAL_DAYS} days free, no card.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              <PricingCards location="home_pricing" />
              <p className="mt-8 text-center">
                <Link href="/pricing" className="font-semibold text-brand hover:underline" data-testid="link-see-full-pricing">
                  See full pricing
                </Link>
              </p>
            </AnimatedSection>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
